/* ──────────────────────────────────────────────────────────────
   src/components/GenericExportDialog.tsx
   Diálogo genérico para exportar Excel
   · Usa query-string con la key **roles** y la lista **fields**.
   · Hace la petición con axios (incluye automáticamente el token)
     y descarga el blob para evitar el 401 del guard JWT.
   ────────────────────────────────────────────────────────────── */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  Box,
} from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import api from "../api/axios"; // instancia axios → ya inyecta Authorization

/* ───────────────── tipos ───────────────── */
export type Field = { key: string; label: string };
export type Filter = { key: string; label: string };

interface Props {
  open: boolean;
  onClose: () => void;
  endpoint: string; // ej. '/usuarios/export' (⚠️ sin '/api')
  entityName: string; // ej. 'usuarios'
  fields: Field[];
  filters?: Filter[]; // ej. roles
}

export default function GenericExportDialog({
  open,
  onClose,
  endpoint,
  entityName,
  fields,
  filters = [],
}: Props) {
  /* estado interno */
  const [selRoles, setSelRoles] = useState<string[]>([]);
  const [selFields, setSelFields] = useState<string[]>(fields.map((f) => f.key));

  const toggle = (arr: string[], v: string, set: (x: string[]) => void) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  /* ────────── exportar ────────── */
  const handleExport = async () => {
    try {
      /* 1· petición con credenciales (Bearer) */
      const { data: blob } = await api.get(endpoint, {
        params: {
          ...(selRoles.length ? { roles: selRoles.join(",") } : {}),
          fields: selFields.join(","),
        },
        responseType: "blob",
      });

      /* 2· forzar descarga */
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entityName}-${dayjs().format("YYYYMMDD")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      alert("No se pudo generar el archivo. Intenta de nuevo.");
    }
  };

  /* ────────── UI ────────── */
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Exportar {entityName}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* —— filtros / roles —— */}
          {filters.length > 0 && (
            <Box>
              <strong>Filtros</strong>
              <FormGroup row>
                {filters.map((f) => (
                  <FormControlLabel
                    key={f.key}
                    control={
                      <Checkbox
                        checked={selRoles.includes(f.key)}
                        onChange={() => toggle(selRoles, f.key, setSelRoles)}
                      />
                    }
                    label={f.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {/* —— campos —— */}
          <Box>
            <strong>Campos</strong>
            <FormGroup row>
              {fields.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={
                    <Checkbox
                      checked={selFields.includes(c.key)}
                      onChange={() => toggle(selFields, c.key, setSelFields)}
                    />
                  }
                  label={c.label}
                />
              ))}
            </FormGroup>

            {/* chips con la selección */}
            <Box mt={1}>
              {selFields.map((f) => (
                <Chip key={f} label={f} size="small" sx={{ mr: 0.5 }} />
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleExport}>
          Exportar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
